import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Form, Table, Row, Alert, Card } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

export default function EditPurchaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [order, setOrder] = useState([]);
  const [orderData, setOrderData] = useState({
    vendorId: "",
    brokerId: "",
    quotationId: "",
    orderDate: "",
    expectedDeliveryDate: "",
    deliveryFrom: "",
    deliveryTo: "",
    tax: 0,
    notes: "",
    paymentTerm: "",
    itemsTotalPrice: 0
  });

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    productId: "",
    quality: "",
    orderedQuantity: 0,
    unit: "",
    rate: 0,
    totalPrice: 0
  });

  const [products, setProducts] = useState([]);
  const [vendorName, setVendorName] = useState("");
  const [brokerName, setBrokerName] = useState("");
  const [quotationCode, setQuotationCode] = useState("");

  // Permission checks
  const canEditPO = useHasPermission(PERMISSIONS.PO_EDIT);
  const canViewPO = useHasPermission(PERMISSIONS.PO_VIEW);
  const canViewProducts = useHasPermission(PERMISSIONS.VIEW_PRODUCTS);

  useEffect(() => {
    const fetchData = async () => {
      if (!canViewPO) {
        toast.error("You don't have permission to view this purchase order");
        navigate("/purchase-orders");
        return;
      }

      try {
        const promises = [
          axios.get(`${config.API_BASE_URL}/purchase-orders/${id}`),
          axios.get(`${config.API_BASE_URL}/purchase-order-items?purchaseOrderId=${id}`)
        ];

        if (canViewProducts) {
          promises.push(axios.get(`${config.API_BASE_URL}/products?populateUnit=true`));
        }

        const responses = await Promise.all(promises);

        const [orderRes, itemsRes, productsRes] = responses;

        if (canViewProducts && productsRes) {
          setProducts(productsRes.data);
        }

        setOrder(orderRes.data.order);
        const order = orderRes.data.order;
        setOrderData({
          vendorId: order.vendorId,
          brokerId: order.brokerId,
          quotationId: order.quotationId,
          orderDate: order.orderDate?.split("T")[0] || "",
          expectedDeliveryDate: order.expectedDeliveryDate?.split("T")[0] || "",
          deliveryFrom: order.deliveryFrom || "",
          deliveryTo: order.deliveryTo || "",
          tax: order.tax || 0,
          notes: order.notes || "",
          paymentTerm: order.paymentTerm || "",
          itemsTotalPrice: order.itemsTotalPrice || 0
        });

        setItems(itemsRes.data || []);
        setVendorName(orderRes.data.vendor?.business_name || "");
        setBrokerName(orderRes.data.broker?.business_name || "");
        setQuotationCode(orderRes.data.quotation?.quotationCode || "");
      } catch (err) {
        console.error("Error fetching purchase order:", err);
        toast.error("Error loading purchase order");
        navigate("/purchase-orders");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, canViewPO, canViewProducts, navigate]);

  const handleOrderChange = (e) => {
    setOrderData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    let updatedItem = { ...newItem, [name]: value };

    if (name === "productId") {
      const prod = products.find((p) => p._id === value);
      updatedItem.unit = prod?.unitId?.unitCode || "";
    }

    if (name === "orderedQuantity" || name === "rate") {
      const qty = name === "orderedQuantity" ? value : updatedItem.orderedQuantity;
      const rate = name === "rate" ? value : updatedItem.rate;
      updatedItem.totalPrice = parseFloat(qty || 0) * parseFloat(rate || 0);
    }

    setNewItem(updatedItem);
  };

  const addItem = () => {
    if (!canEditPO) {
      toast.error("You don't have permission to modify items");
      return;
    }

    if (!newItem.productId || newItem.orderedQuantity <= 0 || newItem.rate <= 0) {
      toast.error("Please fill product, quantity, and unit price");
      return;
    }
    setItems((prev) => [...prev, newItem]);
    setNewItem({
      productId: "",
      quality: "",
      orderedQuantity: 0,
      unit: "",
      rate: 0,
      totalPrice: 0
    });
  };

  const removeItem = (index) => {
    if (!canEditPO) {
      toast.error("You don't have permission to modify items");
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const validateStep1 = () => {
    if (!orderData.expectedDeliveryDate) {
      toast.error("Please select expected delivery date");
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!canEditPO) {
      toast.error("You don't have permission to update this purchase order");
      return;
    }

    if (!validateStep1()) return;

    setSaving(true);
    try {
      // Calculate grand total
      const subtotal = items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
      const taxAmount = subtotal * ((parseFloat(orderData.tax) || 0) / 100);
      const grandTotal = subtotal + taxAmount;

      // 1️⃣ Update purchase order details
      await axios.put(`${config.API_BASE_URL}/purchase-orders/${id}`, {
        expectedDeliveryDate: orderData.expectedDeliveryDate,
        orderDate: orderData.orderDate,
        deliveryFrom: orderData.deliveryFrom,
        deliveryTo: orderData.deliveryTo,
        tax: orderData.tax,
        notes: orderData.notes,
        paymentTerm: orderData.paymentTerm,
        itemsTotalPrice: grandTotal
      });

      // 2️⃣ Remove old items
      await axios.delete(`${config.API_BASE_URL}/purchase-order-items/by-order/${id}`);

      // 3️⃣ Insert updated items
      await Promise.all(
        items.map(it =>
          axios.post(`${config.API_BASE_URL}/purchase-order-items/new`, {
            ...it,
            purchaseOrderId: id
          })
        )
      );

      // toast.success("Purchase Order updated successfully");
      setSaving(false);
      navigate("/purchase-orders", {
        state: { toast: "Purchase Order updated successfully" }
      });
    } catch (err) {
      console.error("Error updating purchase order", err);
      toast.error("Error updating purchase order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // If user can't edit PO, show permission denied
  if (!canEditPO && !loading) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to={`/purchase-orders`}>Purchase Orders</Link></li>
            <li className="breadcrumb-item"><Link to={`/purchase-orders/${order._id}`}>#{order.orderId}</Link></li>
            <li className="breadcrumb-item active">Edit Access Denied</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Edit Access Denied</strong>
            <p className="mb-0">You don't have permission to edit purchase orders. You can view the order details instead.</p>
          </div>
        </Alert>
        <div className="mt-3">
          <Link to={`/purchase-orders/${id}`} className="btn btn-primary me-2">
            <i className="fas fa-eye me-1"></i>View Purchase Order
          </Link>
          <Link to="/purchase-orders" className="btn btn-secondary">
            <i className="fas fa-arrow-left me-1"></i>Back to List
          </Link>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center mt-5">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to={`/purchase-orders`}>Purchase Orders</Link></li>
          <li className="breadcrumb-item"><Link to={`/purchase-orders/${order._id}`}>#{order.orderId}</Link></li>
          <li className="breadcrumb-item active">Edit</li>
        </ol>
      </nav>

      <Row className="mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Edit Purchase Order #{order.orderId}</h2>
              <small className="text-muted">Step {step} of 2 • Last updated: {new Date(order.updatedAt || order.createdAt).toLocaleDateString()}</small>
            </div>
            <div className="progress" style={{ width: '200px', height: '6px' }}>
              <div
                className="progress-bar"
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          {step === 1 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-edit me-2"></i>Order Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <div className="row">
                    {/* Read-only Vendor */}
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Vendor</Form.Label>
                      <Form.Control type="text" value={vendorName} readOnly className="bg-light" />
                      <Form.Text className="text-muted">
                        <i className="fas fa-lock me-1"></i>Vendor cannot be changed
                      </Form.Text>
                    </Form.Group>

                    {/* Read-only Broker */}
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Broker</Form.Label>
                      <Form.Control type="text" value={brokerName || 'No broker'} readOnly className="bg-light" />
                      <Form.Text className="text-muted">
                        <i className="fas fa-lock me-1"></i>Broker cannot be changed
                      </Form.Text>
                    </Form.Group>

                    {/* Read-only Quotation */}
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Quotation</Form.Label>
                      <Form.Control type="text" value={quotationCode || 'No quotation'} readOnly className="bg-light" />
                      <Form.Text className="text-muted">
                        <i className="fas fa-lock me-1"></i>Quotation cannot be changed
                      </Form.Text>
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Order Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        name="orderDate"
                        value={orderData.orderDate}
                        onChange={handleOrderChange}
                      />
                    </Form.Group>
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>
                        Expected Delivery Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="expectedDeliveryDate"
                        value={orderData.expectedDeliveryDate}
                        onChange={handleOrderChange}
                        // min={new Date().toISOString().split("T")[0]}
                      />
                    </Form.Group>
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Payment Terms</Form.Label>
                      <Form.Control
                        type="text"
                        name="paymentTerm"
                        value={orderData.paymentTerm}
                        onChange={handleOrderChange}
                        placeholder="e.g., Net 30 days"
                      />
                    </Form.Group>
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Other Notes</Form.Label>
                      <Form.Control
                        type="text"
                        name="notes"
                        value={orderData.notes}
                        onChange={handleOrderChange}
                        placeholder="Additional notes"
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery To</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="deliveryTo"
                        value={orderData.deliveryTo}
                        onChange={handleOrderChange}
                        placeholder="Enter delivery address"
                      />
                    </Form.Group>
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery From</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="deliveryFrom"
                        value={orderData.deliveryFrom}
                        onChange={handleOrderChange}
                        placeholder="Enter pickup address"
                      />
                    </Form.Group>
                  </div>

                  <div className="d-flex justify-content-between">
                    <Link to={`/purchase-orders/${id}`} className="btn btn-secondary">
                      <i className="fas fa-arrow-left me-2"></i>Cancel
                    </Link>
                    <Button onClick={() => {
                      if (validateStep1()) {
                        setStep(2);
                      }
                    }}>
                      Next: Edit Items <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-shopping-cart me-2"></i>Order Items
                </h5>
              </Card.Header>
              <Card.Body>
                {!canViewProducts && (
                  <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    You have limited access to products. Contact your administrator for full product access.
                  </Alert>
                )}

                {/* Add Items */}
                <Form className="mb-4 border rounded p-3 bg-light">
                  <h6 className="mb-3">Add New Item</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3 form-floating">
                      <Form.Select
                        name="productId"
                        value={newItem.productId}
                        onChange={handleItemChange}
                        disabled={!canViewProducts}
                      >
                        <option value="">Select Product</option>
                        {products.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.productName}
                          </option>
                        ))}
                      </Form.Select>
                      <label>Product</label>
                    </div>
                    <div className="col-md-2 form-floating">
                      <Form.Control
                        name="quality"
                        placeholder="Quality"
                        value={newItem.quality}
                        onChange={handleItemChange}
                      />
                      <label>Quality</label>
                    </div>
                    <div className="col-md-2 position-relative">
                      <div className="form-floating">
                        <Form.Control
                          type="number"
                          name="orderedQuantity"
                          placeholder="Quantity"
                          value={newItem.orderedQuantity}
                          onChange={handleItemChange}
                          min="0"
                          step="0.01"
                        />
                        <label>Quantity</label>
                      </div>
                      {newItem.unit && (
                        <span
                          className="unit-suffix"
                        >
                          {newItem.unit}
                        </span>
                      )}
                    </div>
                    <div className="col-md-2 form-floating">
                      <Form.Control
                        type="number"
                        name="rate"
                        placeholder="Unit Price"
                        value={newItem.rate}
                        onChange={handleItemChange}
                        min="0"
                        step="0.01"
                      />
                      <label>Unit Price (₹)</label>
                    </div>
                    <div className="col-md-2 form-floating">
                      <Form.Control
                        type="number"
                        value={(newItem.orderedQuantity * newItem.rate).toFixed(2)}
                        readOnly
                        className="bg-light"
                      />
                      <label>Total (₹)</label>
                    </div>
                    <div className="col-md-1">
                      <Button
                        variant="success"
                        onClick={addItem}
                        className="w-100"
                        disabled={!canViewProducts}
                        title={!canViewProducts ? "Limited product access" : "Add item"}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  </div>
                </Form>

                {/* Items Table */}
                {items.length > 0 && (
                  <>
                    <div className="table-responsive">
                      <Table bordered hover className="mb-4">
                        <thead className="table-light">
                          <tr>
                            <th>Product</th>
                            <th>Quality</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                            <th width="80px">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => {
                            const productId = it.productId?._id || it.productId;
                            const product = products.find((p) => p._id === productId);
                            const productName = product?.productName || it.productId?.productName || 'N/A';
                            
                            return (
                              <tr key={i}>
                                <td>{productName}</td>
                                <td>{it.quality || '-'}</td>
                                <td>{it.orderedQuantity} {it.unit}</td>
                                <td>₹{parseFloat(it.rate).toFixed(2)}</td>
                                <td>₹{(it.orderedQuantity * it.rate).toFixed(2)}</td>
                                <td className="text-center">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeItem(i)}
                                    title="Remove item"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    <div className="row justify-content-end">
                      <div className="col-md-6">
                        <Card>
                          <Card.Body>
                            <div className="row mb-2">
                              <div className="col-md-6">
                                <Form.Group>
                                  <Form.Label>Tax (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="tax"
                                    value={orderData.tax}
                                    onChange={(e) =>
                                      setOrderData((prev) => ({ ...prev, tax: e.target.value }))
                                    }
                                    min="0"
                                    max="100"
                                    step="0.01"
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-md-6 d-flex align-items-end">
                                <div className="w-100">
                                  <Form.Label>Subtotal: ₹{items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0).toFixed(2)}</Form.Label>
                                </div>
                              </div>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                              <strong>Grand Total:</strong>
                              <strong className="text-primary fs-4">
                                ₹{(() => {
                                  const subtotal = items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
                                  const taxAmount = subtotal * ((parseFloat(orderData.tax) || 0) / 100);
                                  return (subtotal + taxAmount).toFixed(2);
                                })()}
                              </strong>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  </>
                )}

                {items.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-shopping-cart fa-2x mb-2"></i>
                    <p>No items in this order. Add items to continue.</p>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>Back
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleUpdate}
                    disabled={items.length === 0 || saving}
                  >
                    <i className="fas fa-save me-2"></i>Update Purchase Order
                    {/* {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>Update Purchase Order
                      </>
                    )} */}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </Row>
    </Layout>
  );
}
